import axios from 'axios';
import io from 'socket.io-client';
import { store } from './index.js';
import { requestUpdateAdImage } from './actions/advertisements';
import { getLastActivity, setLastActivity } from './localStorage.js';
import { keycloakRef } from './components/auth/KeycloakProvider';

// GraphQL is served by the Smart Cabinet Root API (same host as the REST endpoints below).
// The old core API (formerly REACT_APP_IPA_API_URL) is no longer called by the portal.
const GRAPHQL_URL = `${process.env.REACT_APP_API_URL}/v4/graphql`;



function getAuthorizationHeader() {
    return keycloakRef.current.updateToken(30)
        .then(() => `Bearer ${keycloakRef.current.token}`)
        .catch(error => {
            console.error('Token refresh failed', error);
            keycloakRef.current.logout();
        });
}

// Wrapping fetch with a custom function that records last activity timestamp at local storage
async function fetchWithActivityUpdate(url, options = {}) {
    const response = await fetch(url, options);
    const lastActivity = getLastActivity();
    if (!lastActivity || new Date() - new Date(lastActivity) > 60000) {
        setLastActivity();
    }
    return response;
}

/**
 * Attach a fresh Keycloak bearer token and return the raw Response, leaving the body
 * untouched.
 *
 * Exported so the `src/services/*` clients can do their own response handling:
 * cloud-discount-service and cloud-core-api return JSON:API errors
 * (`{errors:[{detail}]}`), which `request()` below cannot surface because it only looks for
 * `body.message` — every failure would read as a bare status text with the real reason lost.
 *
 * ⚠️ The token/header/fetch block below is duplicated from `request()` **on purpose** — that
 * function has ~97 call sites across the app and was left byte-for-byte untouched rather than
 * refactored to share this code. The two must stay in sync: any change to the token refresh,
 * the header merge, or the activity tracking has to be made in BOTH places, or the discount
 * services will silently diverge from the rest of the app (e.g. session handling differing on
 * one page only).
 *
 * @param {RequestInfo|URL} url
 * @param {RequestInit?} init
 * @returns {Promise<Response>}
 */
export function authorizedFetch(url, init) {
    return getAuthorizationHeader()
        .then(Authorization => fetchWithActivityUpdate(
            url,
            { ...init, headers: { Authorization, ...(init?.headers || {}) } }
        ));
}

/**
 * ⚠️ Keep the token/header/fetch block below in sync with `authorizedFetch` above — it is
 * duplicated deliberately so this function, used by ~97 call sites, stayed untouched.
 *
 * @param {RequestInfo|URL} url
 * @param {RequestInit?} init
 * @returns {Promise}
 */
function request(url, init) {
    return getAuthorizationHeader()
        .then(Authorization => fetchWithActivityUpdate(
            url,
            { ...init, headers: { Authorization, ...(init?.headers || {}) } }
        ))
        .then(response => {
            const contentType = response.headers.get('Content-Type');
            
            // note: example graphql response content: { ok: true, status: 200 ... }
            if (response.ok) {
                if (!contentType) {
                    return;
                }

                if (contentType.match(/application\/json/g)) {
                    return response.json();
                }

                return response.text();
            }

            if (contentType?.match(/application\/json/g)) {
                return response.json()
                    .then(body => {
                        const error = body.message
                            ? new Error(body.message)
                            : new Error(response.statusText);

                        error.response = response;
                        return Promise.reject(error);
                    });
            }

            const error = new Error(response.statusText);
            error.response = response;
            return Promise.reject(error);
        });
}

export function emitEventToSocketIO(topic) {
    return new Promise((resolve, reject) => {
        const client = io(process.env.REACT_APP_SOCKETIO_SERVER || 'https://socketio.selflystore.com');
        client.on('connect', () => {
            client.emit(topic, '1');
            client.close();
            resolve();
        });
        client.on('error', () => {
            console.error('connected socket error io');
            reject();
        });
    });
}

/**
 * Fetches the user profile.
 * /me
 */
export function fetchProfile() {
    return request(`${process.env.REACT_APP_API_URL}/v4/me`);
}

/**
 * Fetches the product catalog.
 *
 * @returns {Promise<Array>} products
 */
export function fetchProducts(group) {
    const url = group
        ? `${process.env.REACT_APP_API_URL}/v1/products?group=${encodeURIComponent(group.Id)}`
        : `${process.env.REACT_APP_API_URL}/v1/products`;

    return request(url);
}

/**
 * @returns {Promise<Array>} products
 */
export function fetchSingleProduct(group, productBarcode) {
    const url = group
        ? `${process.env.REACT_APP_API_URL}/v1/products/${productBarcode}?group=${encodeURIComponent(group.Id)}`
        : `${process.env.REACT_APP_API_URL}/v1/products/${productBarcode}`;

    return request(url);
}

/**
 * Fetches the product types.
 *
 * @returns {Promise<Array>} productTypes
 */
export function fetchProductTypes() {
    return request(`${process.env.REACT_APP_API_URL}/v1/encoding-instruction`);
}

/**
 * Fetches the advertisements.
 *
 * @returns {Promise<Array>} advertisements
 */
export function fetchAdvertisements(group) {
    const url = group
        ? `${process.env.REACT_APP_API_URL}/v1/advertisements?group=${encodeURIComponent(group.Id)}`
        : `${process.env.REACT_APP_API_URL}/v1/advertisements`;

    return request(url);
}

/**
 * Sends HTTP request to create new product.
 *
 * @returns {Promise<Product>} product
 */
export function createProduct(product, group) {
    const url = group
        ? `${process.env.REACT_APP_API_URL}/v1/products?group=${encodeURIComponent(group.Id)}`
        : `${process.env.REACT_APP_API_URL}/v1/products`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify(product),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends HTTP request to update existing product.
 *
 * @param {string} productBarcode
 * @param {Product} product Product should not include barcode or product id (check api spec for what is allowed)
 * @param {Object} group user group
 * @returns {Promise<Product>} product
 */
export function updateProduct(productBarcode, product, group) {
    const url = group
        ? `${process.env.REACT_APP_API_URL}/v1/products/${productBarcode}?group=${encodeURIComponent(group.Id)}`
        : `${process.env.REACT_APP_API_URL}/v1/products/${productBarcode}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(product),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export function archiveProduct(productBarcode, product, group) {
    const url = group
        ? `${process.env.REACT_APP_API_URL}/v1/products/${productBarcode}?group=${encodeURIComponent(group.Id)}`
        : `${process.env.REACT_APP_API_URL}/v1/products/${productBarcode}`;

    return request(url, {
        method: 'PATCH',
        body: JSON.stringify({ status: product.status }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends HTTP request to store product image and returns url to image
 *
 * @returns {Promise} formData Form data with 1 image
 */
export function createProductImage(formData, group) {
    const url = `${process.env.REACT_APP_API_URL}/v1/product-image?group=${encodeURIComponent(group)}`;

    return request(url, {
        method: 'POST',
        body: formData,
    });
}

/**
 * Sends HTTP request to store logo image and returns url to image
 *
 * @returns {Promise} formData Form data with 1 image
 */
export function createLogoImage(formData, group) {
    const url = `${process.env.REACT_APP_API_URL}/v1/logo-image?group=${encodeURIComponent(group.Id)}`;

    return request(url, {
        method: 'POST',
        body: formData,
    });
}

/**
 * Sends HTTP request to store product image and returns url to image
 *
 * @returns {Promise<Object>} formData Form data with 1 image
 */
export function createAdImage(formData, group) {
    const url = `${process.env.REACT_APP_API_URL}/v1/advertisement-image?group=${encodeURIComponent(group)}`;

    return request(url, {
        method: 'POST',
        body: formData,
    });
}

/**
 * upload the file to the server
 */
export async function createAdMedia(blobChunck, group) {
    const url = `${process.env.REACT_APP_API_URL}/v1/advertisement-media?group=${encodeURIComponent(group.Id)}`;
    const Authorization = await getAuthorizationHeader();
    
    const fetchData = await axios.post(url, blobChunck, {
        headers: { Authorization, },
        onUploadProgress: function(progressEvent) {
            const { loaded, total } = progressEvent;
            const percentage = Math.ceil((loaded / total) * 100);
            store.dispatch(requestUpdateAdImage(percentage));
        },
    });
    
    if (fetchData.status === 201) {
        return fetchData.data;
    } else {
        return new Error(fetchData.statusText);
    }
}

/**
 * Sends HTTP request to update existing ad
 *
 * @param {string} adId
 * @param {Object} ad Ad should not include id (check api spec for what is allowed)
 * @param {Object} group
 * @returns {Promise} promise
 */
export function updateAd(adId, ad, group) {
    const url = group
        ? `${process.env.REACT_APP_API_URL}/v1/advertisements/${adId}?group=${encodeURIComponent(group.Id)}`
        : `${process.env.REACT_APP_API_URL}/v1/advertisements/${adId}`;
    
    return request(url, {
        method: 'PUT',
        body: JSON.stringify(ad),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends HTTP request to deleting ad
 *
 * @param {string} adId
 * @param {Object} group
 * @returns {Promise} promise
 */
export function deleteAd(adId, group) {
    const url = group
        ? `${process.env.REACT_APP_API_URL}/v1/advertisements/${adId}?group=${encodeURIComponent(group.Id)}`
        : `${process.env.REACT_APP_API_URL}/v1/advertisements/${adId}`;
    
    return request(url, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Fetches the cabinet inventory.
 *
 * @returns {Promise<Object>} cabinetInventory
 */
export function fetchCabinetInventory(cabinetCode) {
    return request(`${process.env.REACT_APP_API_URL}/v1/inventory/${cabinetCode}`);
}

/**
 * Fetches the cabinet current status.
 *
 * @returns {Promise<Object>} cabinetInventory
 */
export function fetchDeviceStatus(deviceCode) {
    // fetch current status of the selected cabinet
    // graphQL request to IPA API
    const graphqlQuery = `{device(code: "${deviceCode}") {id name code type subType lastTimeSeenOnline isOnline currentState{values(namesCsv: "lock,door")}  }}`;
    
    return request(GRAPHQL_URL, {
        method: 'POST',
        body: JSON.stringify({ query: graphqlQuery }),
        headers: {
            'Content-Type': 'application/json',
        },
    })
        .then(body => {
            // missing data due to GraphQL error
            if (!body.data) {
                const error = new Error('Unable to fetch device due to GraphQL error');
                error.graphQLErrors = body.errors;
                return Promise.reject(error);
            }

            // location doesn't exist
            if (body.data.device === null) {
                return Promise.reject(new Error(`No device found with device code: ${deviceCode}`));
            }
            
            const cabinet = body.data.device;
            if (cabinet.currentState && cabinet.currentState.values) {
                cabinet.isLocked = cabinet.currentState.values[0] && cabinet.currentState.values[0].isLocked === 'true';
                cabinet.isOpened = cabinet.currentState.values[1] && cabinet.currentState.values[1].isOpened === 'true';
            }
            cabinet.deviceCode = cabinet.code;

            return Promise.resolve(cabinet);
        });
}

/**
 * Fetches the cabinet configuration.
 *
 * @returns {Promise<Object>} cabinetInventory
 */
export function fetchDeviceConfiguration(deviceCode) {
    // fetch current status of the selected cabinet
    const graphqlQuery = `{device(code: "${deviceCode}") { alternativeIds } }`;
    
    return request(GRAPHQL_URL, {
        method: 'POST',
        body: JSON.stringify({ query: graphqlQuery }),
        headers: {
            'Content-Type': 'application/json',
        },
    })
        .then(body => {
            // missing data due to GraphQL error
            if (!body.data) {
                const error = new Error('Unable to fetch device due to GraphQL error');
                error.graphQLErrors = body.errors;
                return Promise.reject(error);
            }

            // location doesn't exist
            if (body.data.device === null) {
                return Promise.reject(new Error(`No device found with device code: ${deviceCode}`));
            }
            
            return Promise.resolve(body.data.device.alternativeIds);
        });
}

/**
 * Updates the cabinet settings to detect tag quality and ignore bad tags.
 */
export function updateTagQualityMetrics(
    deviceCode,
    detectTagQuality = false,
    ignoreBadTags = false,
    acceptablePercentageOfBadTags,
    chargeSoldTags,
    chargeUnreplenishedTags,
    cabinetTagSensitivityLevel
) {
    // Same root API endpoint as updateCabinetSettings: these are plain cabinet settings keys.
    const url = `${process.env.REACT_APP_API_URL}/v3/device-settings/${deviceCode}`;

    return request(url, {
        method: 'PATCH',
        body: JSON.stringify({
            detectTagQuality,
            ignoreBadTags,
            acceptablePercentageOfBadTags,
            chargeSoldTags,
            chargeUnreplenishedTags,
            cabinetTagSensitivityLevel,
        }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Update cabinet settings
 * @param {*} id
 * @param {*} settings
 */
export function updateCabinetSettings(deviceCode, settings = {}) {
    // Root API merges the given keys into the cabinet settings; a null value removes a key.
    const url = `${process.env.REACT_APP_API_URL}/v3/device-settings/${deviceCode}`;

    return request(url, {
        method: 'PATCH',
        body: JSON.stringify(settings),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Fetches cabinets.
 *
 * @returns {Promise<Array>} cabinets
 */
export function fetchCabinets(group) {
    const url = group 
        ? `${process.env.REACT_APP_API_URL}/v1/cabinets?group=${encodeURIComponent(group.Id)}` 
        : `${process.env.REACT_APP_API_URL}/v1/cabinets`;
    
    return request(url);
}

/**
 * Sends command to cabinet
 *
 * @returns {Promise} product
 */
export function sendCommand(deviceCode, command, parameters) {
    const url = `${process.env.REACT_APP_API_URL}/v1/command`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify({ deviceCode: deviceCode, command: command, params: parameters }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Fetches tags that have been removed from cabinet.
 *
 * @param {string} deviceCode
 * @param {string|null} from (YYYY-MM-DD) time range start time
 * @param {string|null} to (YYYY-MM-DD) time range end time
 * @param {string|null} status (optional) {DONE, CANCEL, ERROR, TIMEOUT}
 *
 * @returns {Promise} transactions
 */
export function fetchTransactions(deviceCode, from = null, to = null, status = null) {
    const url = new URL(`${process.env.REACT_APP_API_URL}/v1/transactions/${deviceCode}`);
    const params = { from, to, status };
    // Clean undefined keys
    Object.keys(params).forEach(key => !params[key] && delete params[key]);
    url.search = new URLSearchParams(params);

    return request(url, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Makes the refund for the order.
 * @param {*} deviceCode Cabinet device code
 * @param {*} orderId Order ID
 * @param {*} refundItems Items to be refund
 * @param {*} reason Reason of the refund
 */
export async function refundOrder(deviceCode, orderId, refundItems, reason) {
    const url = new URL(`${process.env.REACT_APP_API_URL}/v1/refund/${deviceCode}`);

    return request(url, {
        method: 'PUT',
        body: JSON.stringify({ orderId, refundItems, reason }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Retries a failed/partial wiPay payment transaction.
 * @param {*} orderId Order ID of the transaction to retry
 */
export function retryPayment(orderId) {
    const url = `${process.env.REACT_APP_API_URL}/v3/payment/retry`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify({ orderId }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Fetches the cabinet refills.
 *
 * @returns {Promise} cabinetInventory
 */
export function fetchCabinetRefills(deviceCode, params) {
    const url = new URL(`${process.env.REACT_APP_API_URL}/v2/refill/${deviceCode}`);

    if (params && Object.keys(params).length) {
        url.search = new URLSearchParams(params);
    }

    return request(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends HTTP request to set cabinet location.
 *
 * @param {string} deviceCode
 * @param {string} locationId
 * @param {Object} group
 */
export function setCabinetLocation(deviceCode, locationId, group) {
    const url = group
        ? `${process.env.REACT_APP_API_URL}/v1/cabinets/${deviceCode}?group=${encodeURIComponent(group.Id)}`
        : `${process.env.REACT_APP_API_URL}/v1/cabinets/${deviceCode}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify({ locationId }),
        headers: {
            'Content-Type': 'application/json',
        },
    })
        .then(body => {
            // Notify root api to notify cabinet backend to update cabinet location
            sendCommand(deviceCode, 'notifyCabinetToRefetchLocation', { value: true })
                .catch(err => console.error(err));
            return body;
        });
}

/**
 * Runs cubejs query end fetches result
 *
 * @param {object} queryObject CubeJS query object
 * @param {string} group Organization id & name
 * @param {number} tries
 * @param {boolean} terminateOnTriesEnd
 * @param {AbortSignal|null} abortSignal
 * @returns {object} cubeQueryResult
 */
export function runCubeQuery(queryObject, group, tries = 0, terminateOnTriesEnd = true, abortSignal = null) {
    if (terminateOnTriesEnd && tries === 5) {
        return Promise.reject('Data loading took too long!');
    }

    let clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (queryObject && queryObject.dimensions && queryObject.dimensions.length && queryObject.dimensions[0].split('.')[0] === 'InventoryHistory') {
        // While asking for inventory history, no timezone is sent as the date needs to stay as it is for each day!
        clientTimeZone = undefined;
    }
    const query = {
        ...queryObject,
        timezone: clientTimeZone,
    };
    const url = new URL(`${process.env.REACT_APP_API_URL}/v1/cube?group=${group.Id}&query=${encodeURIComponent(JSON.stringify(query))}`);

    return request(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        signal: abortSignal ? abortSignal : undefined,
    })
        .then(json => {
            if (json.error && json.error === 'Continue wait') {
                //Instead of re-trying the cube query immediately, giving an interval to avoid overloading to server.
                //It anyway won't help to re-try immediately.
                return new Promise((resolve, reject) => {
                    setTimeout(() => {
                        runCubeQuery(query, group, tries + 1, terminateOnTriesEnd, abortSignal)
                            .then(res => resolve(res))
                            .catch(res => reject(res));
                    }, 8000); // 8 seconds interval
                });
            }
            return json;
        });
}

/**
 * Get locations
 * @param {string} organization
 */
export function fetchLocations(organization) {
    // fetch locations
    const query = `{
        locations(organization: "${organization}", includeChildren: FLAT, withLocationAccessAncestors: true) {
          id,
          code,
          name,
          status,
          parentLocationCode
          isAncestorOfOwnLocation
        }
    }`;

    return request(GRAPHQL_URL, {
        method: 'POST',
        body: JSON.stringify({ query }),
        headers: {
            'Content-Type': 'application/json',
        },
    })
        .then(body => {
            const locations = body.data.locations.filter(location => location.status !== 'Retired');

            return {
                locations: locations
                    .filter(location => !location.isAncestorOfOwnLocation)
                    .map(location => ({
                        id: location.id,
                        code: location.code,
                        name: location.name,
                        parentLocationCode: location.parentLocationCode,
                    })),
                ancestorLocations: locations
                    .filter(location => location.isAncestorOfOwnLocation)
                    .map(location => ({
                        id: location.id,
                        code: location.code,
                        name: location.name,
                        parentLocationCode: location.parentLocationCode,
                    })),
            };
        });
}

/**
 * Create location
 * @param {string} locationCode
 * @param {string} locationName
 * @param {string} group
 */
export function createLocation(locationCode, locationName, group) {
    const url = `${process.env.REACT_APP_API_URL}/v3/locations?group=${encodeURIComponent(group)}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify({
            code: locationCode,
            name: locationName,
        }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Retire location
 * @param {string} locationCode
 * @param {Object} updateLocationObject Object with fields to update
 * @param {string} group
 */
export function updateLocation(locationCode, updateLocationObject, group) {
    // Only the keys present in the body change; `parentLocationCode: null` detaches the location.
    const url = `${process.env.REACT_APP_API_URL}/v3/locations/${locationCode}?group=${encodeURIComponent(group)}`;

    return request(url, {
        method: 'PATCH',
        body: JSON.stringify(updateLocationObject),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Retires a location (and everything below it) in the root API.
 * Fails with a 400 and a readable message while the location still has active children, cabinets or product prices.
 * @param {string} locationCode
 * @param {string} group Organization id
 */
export function retireLocation(locationCode, group) {
    const url = `${process.env.REACT_APP_API_URL}/v3/locations/${locationCode}?group=${encodeURIComponent(group)}`;

    return request(url, {
        method: 'DELETE',
    });
}

/**
 * Update cabinet name
 * @param {string} deviceCode Code for the device aka cabinet id
 * @param {Object} updateCabinetNameObject
 */
export function updateCabinetName(deviceCode, updateCabinetNameObject = {}) {
    const url = `${process.env.REACT_APP_API_URL}/v1/cabinets/${deviceCode}`;

    return request(url, {
        method: 'PATCH',
        body: JSON.stringify(updateCabinetNameObject),
        headers: {
            'Content-Type': 'application/json',
        },
    })
        .then(() => ({ code: deviceCode, ...updateCabinetNameObject }));
}

/**
 * Sends a POST request to root API to create a new VAT Category
 * @param {object} body The body of POST request containing name and tacValue
 * @param {object} group The organization id where VAT category belongs to. Sent as query parameter
 */
export function createVatCategory(body, group) {
    const url = `${process.env.REACT_APP_API_URL}/v1/vat?group=${encodeURIComponent(group.Id)}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends a GET request to root API to get all VAT Categories belonging to an organization
 * @param {object} group Contains the organization id where VAT categories belongs to. Sent as query parameter
 */
export function fetchVatCategories(group) {
    return request(`${process.env.REACT_APP_API_URL}/v1/vat?group=${group.Id}`);
}

/**
 * Sends a PUT request to root API to update an existing VAT Category
 * @param {string} id The id of the VAT category to be updated sent as path parameter
 * @param {object} body The body of PUT request containing name and tacValue
 * @param {object} group Contains the organization id where VAT categories belongs to. Sent as query parameter
 */
export function updateVatCategory(id, body, group) {
    const url = `${process.env.REACT_APP_API_URL}/v1/vat/${id}?group=${group.Id}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends a DELETE request to root API to remove VAT Category
 * @param {string} id The id of the VAT category to be deleted sent as path parameter
 * @param {object} group Contains the organization id where VAT categories belongs to. Sent as query parameter
 */
export function deleteVatCategory(id, group) {
    const url = `${process.env.REACT_APP_API_URL}/v1/vat/${id}?group=${group.Id}`;

    return request(url, {
        method: 'DELETE',
    });
}

/**
 * Sends a POST request to root API to create a new Product Category
 * @param {object} body The body of POST request containing name and description
 * @param {object} group The organization id where product category belongs to. Sent as query parameter
 */
export function createProductCategory(body, group) {
    const url = `${process.env.REACT_APP_API_URL}/v2/product-category?group=${encodeURIComponent(group.Id)}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends a GET request to root API to get all Product Labels belonging to an organization
 * @param {object} group Contains the organization id where product labels belongs to. Sent as query parameter
 */
export function fetchProductLabels(group) {
    return request(`${process.env.REACT_APP_API_URL}/v3/product-label?group=${group.Id}`);
}

/**
 * Sends a GET request to root API to get all Product Categories belonging to an organization
 * @param {object} group Contains the organization id where product categories belongs to. Sent as query parameter
 */
export function fetchProductCategories(group) {
    return request(`${process.env.REACT_APP_API_URL}/v2/product-category?group=${group.Id}`);
}

/**
 * Sends a PUT request to root API to update an existing Product Category
 * @param {string} id The id of the product category to be updated sent as path parameter
 * @param {object} body The body of PUT request containing name and description
 * @param {object} group Contains the organization id where product category belongs to. Sent as query parameter
 */
export function updateProductCategory(id, body, group) {
    const url = `${process.env.REACT_APP_API_URL}/v2/product-category/${id}?group=${group.Id}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends a DELETE request to root API to remove Product Category
 * @param {number} id The id of the product category to be deleted sent as path parameter
 * @param {object} group Contains the organization id where product category belongs to. Sent as query parameter
 */
export function deleteProductCategory(id, group) {
    const url = `${process.env.REACT_APP_API_URL}/v2/product-category/${id}?group=${group.Id}`;

    return request(url, {
        method: 'DELETE',
    });
}

/**
 * Sends a POST request to root API to create a new supplier
 * @param {object} body The body of POST request containing name and description
 * @param {object} group The organization id where supplier belongs to. Sent as query parameter
 */
export function createSupplier(body, group) {
    const url = `${process.env.REACT_APP_API_URL}/v1/supplier?group=${encodeURIComponent(group.Id)}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends a GET request to root API to get all suppliers belonging to an organization
 * @param {object} group Contains the organization id where suppliers belong to. Sent as query parameter
 */
export function fetchSuppliers(group) {
    return request(`${process.env.REACT_APP_API_URL}/v1/supplier?group=${group.Id}`);
}

/**
 * Sends a PUT request to root API to update an existing Supplier
 * @param {string} id The id of the supplier to be updated sent as path parameter
 * @param {object} body The body of PUT request containing name and description
 * @param {object} group Contains the organization id where supplier belongs to. Sent as query parameter
 */
export function updateSupplier(id, body, group) {
    const url = `${process.env.REACT_APP_API_URL}/v1/supplier/${id}?group=${group.Id}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends a PATCH request to root API to "delete" the supplier. The supplier will not be deleted from the database
 * but the status of the supplier will be be updated so that an organization will no longer see it.
 * @param {Object} supplier
 * @param {Object} group Contains the organization id where suppliers belong to. Sent as query parameter
 */
export function archiveSupplier(supplier, group) {
    const url = `${process.env.REACT_APP_API_URL}/v1/supplier/${supplier.id}?group=${encodeURIComponent(group.Id)}`;

    return request(url, {
        method: 'PATCH',
        body: JSON.stringify({ status: supplier.status }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends a GET request to root API to get all discount rules belonging to organization
 *
 * PERMANENT — this and the two discount writers below back the `/discounts-classic` route, a
 * supported fallback. They are NOT migration leftovers to delete once cloud-discount-service is
 * rolled out. See `services/discountSource.js`.
 * @param {object} group Contains the organization id that discount rules belong to. Sent as query parameter
 */
export function fetchDiscountRules(group) {
    return request(`${process.env.REACT_APP_API_URL}/v2/discounts?group=${group.Id}`);
}

/**
 * Sends a POST request to root API to create a discount rule
 * @param {object} body The body of POST request
 * @param {object} group Contains the organization id that discount rules belong to. Sent as query parameter
 */
export function createDiscountRule(body, group) {
    const url = `${process.env.REACT_APP_API_URL}/v2/discounts?group=${group.Id}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends a UPDATE request to root API to delete a discount rule
 * @param {object} rule Discount rule to update
 * @param {object} group Contains the organization id that discount rules belong to. Sent as query parameter
 */
export function updateDiscountRule(rule, group) {
    const url = `${process.env.REACT_APP_API_URL}/v2/discounts/${rule.Id}?group=${group.Id}`;

    //Delete not needed arguments
    delete rule.Id;
    delete rule.OrganisationId;
    if (!Array.isArray(rule.userIds) || rule.userIds === null) {
        delete rule.userIds;
    }

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(rule),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends a POST request to root API to tell the organisation's cabinets to reload their discounts.
 * The root API stores nothing; it only publishes a `discount-notification created` event on the
 * `organization/<group>` channel (see `src/channels.js` in cabinet.rootapi).
 * @param {object} meta Optional free-form context about the change (action, rule id)
 * @param {object} group Contains the organization id. Sent as query parameter
 */
export function notifyDiscountChange(meta, group) {
    const url = `${process.env.REACT_APP_API_URL}/v3/discount-notification?group=${encodeURIComponent(group.Id)}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify(meta ? { meta } : {}),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends HTTP request to store logo image and returns url to image
 *
 * @returns {Promise} formData Form data with 1 image
 */
export async function uploadDiscountImage(formData, group, discountType) {
    const url = `${process.env.REACT_APP_API_URL}/v3/discount-image?group=${encodeURIComponent(group.Id)}&type=${discountType}`;

    return request(url, {
        method: 'POST',
        body: formData,
    });
}

/**
 * Sends request to fetch device software version to API
 */
export function fetchDeviceSoftwareVersion(deviceId) {
    return request(`${process.env.REACT_APP_API_URL}/v2/device-release-version/${deviceId}`);
}

export function fetchAllSoftwareVersions() {
    return request(`${process.env.REACT_APP_API_URL}/v2/release-version`);
}

export function updateOneOrMoreDeviceSoftwareVersions(group, releaseData) {
    const url = `${process.env.REACT_APP_API_URL}/v2/device-release-version/${releaseData.devices[0].deviceId}?group=${group.Id}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(releaseData),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export function removeReleaseUpdateScheduling(group, scheduleId) {
    const url = `${process.env.REACT_APP_API_URL}/v2/device-release-version/${scheduleId}?group=${group.Id}`;

    return request(url, {
        method: 'DELETE',
    });
}

export function updateDeviceSoftwareVersion(deviceId, version) {
    const url = `${process.env.REACT_APP_API_URL}/v2/device-release-version/${deviceId}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify({ release: version }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export function fetchSelectedDeviceType(deviceId) {
    const query = `{device(hardwareId: "${deviceId}") {type, subType, osVersion }}`;

    return request(GRAPHQL_URL, {
        method: 'POST',
        body: JSON.stringify({ query }),
        headers: {
            'Content-Type': 'application/json',
        },
    })
        .then(body => {
            // missing data due to GraphQL error
            if (!body.data) {
                const error = new Error('Unable to fetch device due to GraphQL error');
                error.graphQLErrors = body.errors;
                return Promise.reject(error);
            }
            // missing device type info in the response
            if (body.data.device === null) {
                return Promise.reject(new Error(`No device found with device code: ${deviceId}`));
            }

            const deviceType = body.data.device;
            // Forcing undefined cabinet sub-type (or subType = None ) to IF2 subtype
            if (deviceType && deviceType.type && deviceType.type === 'smartCabinet' && (deviceType.subType === 'None' || !deviceType.subType)) {
                deviceType.subType = 'IF2';
            }

            return deviceType;
        });
}

/**
 * HTTP request for rebooting cabinet
 */
export function rebootDevice(deviceCode) {
    const url = `${process.env.REACT_APP_API_URL}/v2/device-command?id=${deviceCode}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify({
            deviceCode,
            command: 'reboot',
        }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Faster alternative for fetching temperature. Replacing cube query
 */
export function fetchTemperatureForChart(deviceCode, from, to, group) {
    return request(`${process.env.REACT_APP_API_URL}/v2/device-health?group=${group.Id}&from=${from}&to=${to}&id=${deviceCode}`);
}

/**
 * Sends a GET request to root API to get location hints for organization settings
 * @param {object} group Contains the organization id where organizations belongs to. Sent as query parameter
 */
export function fetchOrganizationSettingsLocationHints(group) {
    return request(`${process.env.REACT_APP_API_URL}/v3/organization/myselfly-settings/location-hints?group=${group?.Id}`);
}

/**
 * Sends a GET request to root API to get all organizations belonging to an organization
 * @param {object} group Contains the organization id where organizations belongs to. Sent as query parameter
 * @param {string|null} locationId
 */
export function fetchOrganizationSettings(group, locationId) {
    const locationParam = locationId ? `&locationId=${locationId}` : '';

    return request(`${process.env.REACT_APP_API_URL}/v3/organization/myselfly-settings?group=${group?.Id}${locationParam}`);
}

/**
 * Update organization settings
 * @param {string} id
 * @param {string|null} locationId
 * @param {object} settings
 */
export function updateOrganizationSettings(id, locationId, settings = {}) {
    const locationParam = locationId ? `&locationId=${locationId}` : '';
    const url = `${process.env.REACT_APP_API_URL}/v3/organization/myselfly-settings/${id}?group=${id}${locationParam}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(settings),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Fetches DES Devices.
 */
export function fetchEncodingDevices(group) {
    const url = group
        ? `${process.env.REACT_APP_API_URL}/v3/encodingDevices?group=${encodeURIComponent(group.Id)}`
        : `${process.env.REACT_APP_API_URL}/v1/cabinets`;

    return request(url);
}

export function fetchAllOrganizationsCabinetReleases(group) {
    return request(`${process.env.REACT_APP_API_URL}/v2/device-release-version?group=${group?.Id}`);
}

/**
 * Fetches the cabinet refills.
 */
export function fetchRefillRules(groupId) {
    return request(`${process.env.REACT_APP_API_URL}/v3/refill-rules?group=${groupId}`);
}

/**
 * request refill rule for a given device code
 */
export function fetchCabinetRefillRule(groupId, deviceCode) {
    return request(`${process.env.REACT_APP_API_URL}/v3/refill-rules/${deviceCode}?group=${groupId}`);
}

/**
 * Sends a UPDATE request to root API to update a refill rule
 * @param {object} rule Discount rule to update
 * @param {string} groupId Contains the organization id that discount rules belong to. Sent as query parameter
 * @param {string} deviceCode
 */
export function createRefillRule(rule, groupId, deviceCode) {
    const url = `${process.env.REACT_APP_API_URL}/v3/refill-rules?group=${groupId}&deviceCode=${deviceCode}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify(rule),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}
/**
 * Sends a UPDATE request to root API to update a refill rule
 * @param {object} rule Discount rule to update
 * @param {string} groupId Contains the organization id that discount rules belong to. Sent as query parameter
 */
export function updateRefillRule(rule, groupId) {
    const url = `${process.env.REACT_APP_API_URL}/v3/refill-rules/${rule.id}?group=${groupId}&deviceCode=${rule.deviceCode}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(rule.data),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends a DELETE request to root API to delete a refill rule
 * @param {string} deviceCode
 * @param {object} groupId Contains the organization id that discount rules belong to. Sent as query parameter
 */
export function deleteRefillRule(deviceCode, groupId) {
    const url = `${process.env.REACT_APP_API_URL}/v3/refill-rules/${deviceCode}?group=${groupId}`;

    return request(url, {
        method: 'DELETE',
    });
}

/**
 * Fetches the cabinet refill plans.
 */
export function fetchRefillPlans(group) {
    return request(`${process.env.REACT_APP_API_URL}/v3/refill-plans?group=${group.Id}`);
}

/**
 * Creates a cabinet refill plan.
 */
export function createRefillPlan(data, group) {
    const url = `${process.env.REACT_APP_API_URL}/v3/refill-plans?group=${group.Id}`;
    // for hot fix in production, I directly change payload before sending to rootapi
    // as save method
    try {
        if (Object.keys(data) && Object.keys(data).length > 0) {
            Object.keys(data).forEach(deviceCode => {
                if (!Array.isArray(data[deviceCode].productData)) return;
                for (let i = 0; i < data[deviceCode].productData.length; i++) {
                    delete data[deviceCode].productData[i].imageUrl;
                    delete data[deviceCode].productData[i].name;
                }
            });
        }
    } catch (e) {
        console.error('can not parse data for refill plan');
    }

    return request(url, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Updates the cabinet refill plan.
 */
export function updateRefillPlan(group, data, id) {
    const url = `${process.env.REACT_APP_API_URL}/v3/refill-plans/${id}?group=${group.Id}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Updates the cabinet refill plan.
 */
export function deleteRefillPlan(group, id) {
    const url = `${process.env.REACT_APP_API_URL}/v3/refill-plans/${id}?group=${group.Id}&planId=true`;

    return request(url, {
        method: 'DELETE',
    });
}

/**
 * Updates the refill plan status.
 */
export function updateRefillPlanStatus(group, status, planId) {
    const url = `${process.env.REACT_APP_API_URL}/v3/refill-plans/${planId}?group=${group.Id}&status=${status}`;

    return request(url, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export async function getRefillSuggestionsByPage(selectedCabinets, group) {
    const splitedCabinets = [];
    const pageSize = 5;

    while (selectedCabinets.length > 0) {
        splitedCabinets.push(selectedCabinets.splice(0, pageSize));
    }

    let data = [];
    for (let i = 0; i < splitedCabinets.length; i++) {
        const resultData = (await postRefillPlanSuggestions(splitedCabinets[i], group).catch(e => e)) || [];
        data = [...data, ...resultData];
    }
    return Promise.resolve(data);
}

/**
 * Posts the refill plan suggestions.
 */
export async function postRefillPlanSuggestions(selectedCabinets, group) {
    const url = `${process.env.REACT_APP_API_URL}/v3/refill-plan-suggestions?group=${group.Id}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify({ selectedCabinets }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 *
 * @param {FormData} formData image data
 * @param {Object} group organisation group
 * @returns
 */
export function createThemeImage(formData, group) {
    const url = `${process.env.REACT_APP_API_URL}/v3/theme-images?group=${group.Id}`;

    return request(url, {
        method: 'POST',
        body: formData,
    });
}

/**
 * Copies/updates the selected settings to the selected cabinets
 * @param {*} selectedCabinets The selected cabinets
 * @param {*} settingType The type of the settings being copied. This is currently only used for the refill rule setting.
 *  If the setting is the refill rule then the value should be 'refillRule' and otherwise the value should be null.
 * @param {*} settings The selected settings to be copied
 * @param {Object} group organisation group
 * @returns Returns an array of the results of each request to copy settings to a cabinet.
 *  The results contain a successful or failure status of each request and if a failure status then also the error of the request.
 */
export async function postSettingsCopyRequest(selectedCabinets, settingType, settings, group) {
    const url = `${process.env.REACT_APP_API_URL}/v1/settings?group=${group.Id}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify({ selectedCabinets, settingType, settings }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Fetch device alarms for the organisation
 */
export async function fetchDeviceAlarms(group) {
    const query = `{alerts(organization: "${group.Id}", state: ACTIVE) { id, type, state, severity, description, device_Id, device_Code, firstTimeSeen, lastTimeSeen}}`;

    return request(GRAPHQL_URL, {
        method: 'POST',
        body: JSON.stringify({ query, }),
        headers: {
            'Content-Type': 'application/json',
        },
    })
        .then(body => {
            // missing data due to GraphQL error
            if (!body.data) {
                const error = new Error('Unable to fetch device alarms due to GraphQL error');
                error.graphQLErrors = body.errors;
                return Promise.reject(error);
            }

            return body.data.alerts || [];
        });
}

/**
 * Sends HTTP request to store plaogram image - it should return an image url.
 */
export function imageUpload(formData, groupId, type) {
    const url = `${process.env.REACT_APP_API_URL}/v3/image-upload?group=${encodeURIComponent(groupId)}&type=${type}`;

    return request(url, {
        method: 'POST',
        body: formData,
    });
}

export function createAutomation(data, group) {
    const url = `${process.env.REACT_APP_API_URL}/v3/automation?group=${group.Id}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export function fetchAutomation(type, group, username) {
    return request(`${process.env.REACT_APP_API_URL}/v3/automation?group=${group.Id}&type=${type}&username=${username}`);
}

export function updateAutomation(id, data, group) {
    const url = `${process.env.REACT_APP_API_URL}/v3/automation/${id}?group=${group.Id}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends a POST request to root API to create a new product surcharge.
 * @param {object} body The body of POST request containing new surcharge information
 * @param {object} group The organization id where product surcharge belongs to.
 */
export function createProductSurcharge(body, group) {
    const url = `${process.env.REACT_APP_API_URL}/v3/product-surcharge?group=${encodeURIComponent(group.Id)}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export function checkRefillAutomation(data, group) {
    const url = `${process.env.REACT_APP_API_URL}/v3/automation/check?group=${group.Id}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends a GET request to root API to get all product surcharges belonging to an organization.
 * @param {object} group Contains the organization id where product surcharges belongs to. Sent as query parameter
 */
export function fetchProductSurcharges(group) {
    const url = `${process.env.REACT_APP_API_URL}/v3/product-surcharge?group=${group.Id}`;

    return request(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Fetch device alarms for the organisation
 */
export async function fetchPlanogram(deviceCode, groupId) {
    return request(`${process.env.REACT_APP_API_URL}/v3/planogram/${deviceCode}?group=${groupId}`);
}

/**
 * Sends a PUT request to root API to update an existing product surcharge
 * @param {string} id The id of the product surcharge to be updated sent as path parameter
 * @param {object} body The body of PUT request containing new information for the exising product surcharge
 * @param {object} group Contains the organization id where product surcharge belongs to.
 */
export function updateProductSurcharge(id, body, group) {
    const url = `${process.env.REACT_APP_API_URL}/v3/product-surcharge/${id}?group=${group.Id}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Update planogram.
 */
export function savePlanogram(deviceCode, groupId, data, overWrite) {
    const modeParam = overWrite ? '&mode=overWrite' : '';
    const url = `${process.env.REACT_APP_API_URL}/v3/planogram/${deviceCode}?group=${groupId}${modeParam}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Sends a DELETE request to root API to remove an existing product surcharge
 * @param {string} id The id of the product surcharge to be deleted sent as path parameter
 * @param {object} group Contains the organization id where product surcharge belongs to.
 */
export function deleteProductSurcharge(id, group) {
    const url = `${process.env.REACT_APP_API_URL}/v3/product-surcharge/${id}?group=${group.Id}`;

    return request(url, {
        method: 'DELETE',
    });
}

export function deletePlanogram(deviceCode, groupId) {
    const url = `${process.env.REACT_APP_API_URL}/v3/planogram/${deviceCode}?group=${groupId}`;

    return request(url, {
        method: 'DELETE',
    });
}

export function fetchUserKeys(groupId) {
    return request(`${process.env.REACT_APP_API_URL}/v3/user-keys?group=${groupId}`);
}

export function createUserKey(groupId, data) {
    const url = `${process.env.REACT_APP_API_URL}/v3/user-keys?group=${groupId}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export function updateUserKey(groupId, data, userKeyId) {
    const url = `${process.env.REACT_APP_API_URL}/v3/user-keys/${userKeyId}?group=${groupId}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export function fetchOrganizationGeneralSettings(groupId) {
    return request(`${process.env.REACT_APP_API_URL}/v3/organization/settings/${encodeURIComponent(groupId)}`);
}

export function updateOrganizationGeneralSettings(groupId, settings) {
    const url = `${process.env.REACT_APP_API_URL}/v3/organization/settings/${encodeURIComponent(groupId)}`;

    return request(url, {
        method: 'PUT',
        body: JSON.stringify(settings),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export function fetchBlacklist(groupId) {
    return request(`${process.env.REACT_APP_API_URL}/v3/blacklist?group=${groupId}`);
}

/**
 * @param {string} groupId
 * @param {string} customerId
 * @param {string} deviceCode
 * @param {string} paymentMethod
 * @param {string} orderId
 * @returns {Promise}
 */
export function createBlacklistItem(groupId, customerId, deviceCode, paymentMethod, orderId) {
    const url = `${process.env.REACT_APP_API_URL}/v3/blacklist?group=${groupId}`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify({
            customerId,
            deviceCode,
            paymentMethod,
            orderId,
            unpaidAmount: 0, 
        }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * @param {string} groupId
 * @param {string} blacklistItemId
 * @returns {Promise}
 */
export function deleteBlacklistItem(groupId, blacklistItemId) {
    const url = `${process.env.REACT_APP_API_URL}/v3/blacklist/${blacklistItemId}?group=${groupId}`;

    return request(url, {
        method: 'DELETE',
    });
}

export function rebootPaymentPos(deviceCode, paymentProvider) {
    const url = `${process.env.REACT_APP_API_URL}/v3/payment/pos`;

    return request(url, {
        method: 'POST',
        body: JSON.stringify({ deviceCode, paymentProvider, command: 'reboot' }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}
